// Who a policy actually covers, on a given day (handoff v08 #18) — the ONE place that decides it. Every coverage view
// (family matrix and the green «covered» chips, the Coverage Radar, the protection-gap map and its campaign segments,
// discussion topics, the protection map's cover lines, client reports) reads the answer from here, so a child insured on
// the father's medical policy is covered everywhere or nowhere.
//
// Rules:
//  - a person is covered by a policy when they are an INSURED person of it: a listed insured party, the policy's
//    insuredPersonId, or the holder when the policy names no insured person at all. A holder who only pays (insured
//    parties are the children) is NOT covered by it.
//  - the policy must be in force on the day: status active/renewed, start (or issue) date on or before the day and end
//    date, when recorded, on or after it. Offers and pending applications are reported apart (`pending`), never as cover.
//  - the policy's category applies to every insured person; a benefit recorded for one member (benefit.insuredPersonId)
//    adds its area to that member only, never to the others.
// Children (handoff #15) come from the household AND the Family tab (family_links, both directions), once per person.
import type { WorkspaceView } from './changeset.ts';
import { ageOn, type Day } from './dates.ts';
import { insuredIds, policyCategory } from './policy-engine.ts';
import { isActivePolicy } from './rules.ts';
import type { Person, Policy, PolicyBenefit, PolicyCategory } from './schema.ts';

export type CoverState = 'in_force' | 'pending' | 'not_started' | 'ended' | 'inactive';
/** the policy's state on `today`: in force only when its status is live and the day falls within its dates */
export function policyCoverState(p: Pick<Policy, 'status' | 'startDate' | 'issueDate' | 'endDate'>, today: Day): CoverState {
  if (p.status === 'offer' || p.status === 'pending') return 'pending';
  if (!isActivePolicy(p)) return 'inactive';
  const start = (p.startDate ?? p.issueDate ?? '').slice(0, 10);
  if (start && start > today) return 'not_started';
  const end = (p.endDate ?? '').slice(0, 10);
  if (end && end < today) return 'ended';
  return 'in_force';
}

/** benefits that apply to this person: the policy-wide ones and the ones recorded for them */
export const benefitsFor = (p: Pick<Policy, 'benefits'>, personId: string): PolicyBenefit[] => (p.benefits ?? []).filter((b) => !b.insuredPersonId || b.insuredPersonId === personId);

const BENEFIT_CATEGORY: [RegExp, PolicyCategory][] = [
  [/death|life/i, 'life'], [/critical/i, 'critical_illness'], [/disab|income|waiver/i, 'disability'], [/medical|hospital/i, 'medical'],
  [/accident/i, 'accident'], [/mortgage|credit|loan/i, 'credit_life'], [/pension|retire/i, 'pension'], [/educat/i, 'education'], [/saving|maturity|fund/i, 'savings'],
];
/** the category a benefit line stands for (null when its kind says nothing, e.g. «other») */
export const benefitCategory = (b: Pick<PolicyBenefit, 'kind'>): PolicyCategory | null => BENEFIT_CATEGORY.find(([re]) => re.test(b.kind ?? ''))?.[1] ?? null;

export interface PersonCover {
  policy: Policy;
  state: 'in_force' | 'pending';
  /** the person is also the policyholder */
  holder: boolean;
  /** benefits that apply to this person (policy-wide + member-specific) */
  benefits: PolicyBenefit[];
  /** the policy's category plus the categories of the benefits that apply to this person */
  categories: PolicyCategory[];
}
export interface CoverOptions { /** also return offers / pending applications (state 'pending') */ pending?: boolean }

/** the cover one policy gives one person on `today`, or null when it does not cover them */
export function policyCoverFor(p: Policy, personId: string, today: Day, opts: CoverOptions = {}): PersonCover | null {
  if (!insuredIds(p).includes(personId)) return null;
  const st = policyCoverState(p, today);
  if (st !== 'in_force' && !(opts.pending && st === 'pending')) return null;
  const benefits = benefitsFor(p, personId);
  const categories = [...new Set([policyCategory(p), ...benefits.map(benefitCategory).filter((c): c is PolicyCategory => !!c)])];
  return { policy: p, state: st, holder: p.personId === personId, benefits, categories };
}

// insured person id → policies naming them, built once per policies table. The store replaces the table array on every
// change (copy-on-write), so the array itself is a safe cache key: a book of 2,500 people stays one pass, not one per person.
const insuredIndexCache = new WeakMap<readonly Policy[], Map<string, Policy[]>>();
function policiesInsuring(v: WorkspaceView, personId: string): readonly Policy[] {
  const rows = v.rows('policies');
  let m = insuredIndexCache.get(rows);
  if (!m) {
    m = new Map();
    for (const p of rows) for (const id of insuredIds(p)) { const a = m.get(id); if (a) a.push(p); else m.set(id, [p]); }
    insuredIndexCache.set(rows, m);
  }
  return m.get(personId) ?? [];
}
/** every policy that covers the person on `today` (as an insured person), each once */
export function coverageForPerson(v: WorkspaceView, personId: string, today: Day, opts: CoverOptions = {}): PersonCover[] {
  const out: PersonCover[] = [];
  for (const p of policiesInsuring(v, personId)) { const c = policyCoverFor(p, personId, today, opts); if (c) out.push(c); }
  return out;
}
/** the same answer for the whole book in one pass over the policies (person id → cover) */
export function coverageIndex(v: WorkspaceView, today: Day, opts: CoverOptions = {}): Map<string, PersonCover[]> {
  const out = new Map<string, PersonCover[]>();
  for (const p of v.rows('policies')) for (const id of insuredIds(p)) {
    const c = policyCoverFor(p, id, today, opts);
    if (!c) continue;
    const a = out.get(id);
    if (a) a.push(c); else out.set(id, [c]);
  }
  return out;
}
/** categories the person holds cover for (in-force entries only) */
export const coveredCategories = (cover: readonly PersonCover[]): Set<PolicyCategory> => new Set(cover.filter((c) => c.state === 'in_force').flatMap((c) => c.categories));
/** policies the person holds (pays for) that are in force on `today`, whoever they insure — what the client provides, not their own cover */
export const heldInForce = (v: WorkspaceView, personId: string, today: Day): Policy[] => v.where('policies', 'personId', personId).filter((p) => policyCoverState(p, today) === 'in_force');
/**
 * Is a plan of this category recorded for the family: covering the person, covering one of their children, or held by
 * the person for someone (a parent owns the child's education plan). Used for family needs (education), not for
 * «is this person covered».
 */
export function familyPlanRecorded(v: WorkspaceView, personId: string, today: Day, cat: PolicyCategory, kids: readonly ChildRef[] = childrenOfPerson(v, personId, today)): boolean {
  if (isCoveredFor(v, personId, today, cat)) return true;
  if (heldInForce(v, personId, today).some((p) => policyCategory(p) === cat)) return true;
  return kids.some((k) => !!k.personId && isCoveredFor(v, k.personId, today, cat));
}
/** is the person covered for a category on `today` */
export const isCoveredFor = (v: WorkspaceView, personId: string, today: Day, cat: PolicyCategory): boolean => coveredCategories(coverageForPerson(v, personId, today)).has(cat);

// --- children (household + Family tab) --------------------------------------------------------
export interface ChildRef { personId: string | null; name: string; birthDate: Day | null; age: number | null; source: 'household' | 'link' | 'reverse'; linkId: string | null }
/**
 * The person's children from every place they are recorded: household members with role child (and dependant when
 * `dependents` is set), family links «child» on this card, and links «parent» recorded on the child's card.
 * One entry per person (a child in the household and in the Family tab counts once); name-only links count too.
 */
export function childrenOfPerson(v: WorkspaceView, personId: string, today: Day, opts: { dependents?: boolean } = {}): ChildRef[] {
  const me = v.get('persons', personId);
  if (!me) return [];
  const out = new Map<string, ChildRef>();
  const live = (id: string | null | undefined): Person | undefined => { const x = id ? v.get('persons', id) : undefined; return x && !x.archivedAt ? x : undefined; };
  const add = (k: string, c: Omit<ChildRef, 'age'>) => {
    const prev = out.get(k);
    const birthDate = c.birthDate ?? prev?.birthDate ?? null;
    out.set(k, { ...(prev ?? c), birthDate, age: birthDate ? ageOn(birthDate, today) : null, linkId: prev?.linkId ?? c.linkId });
  };
  if (me.householdId) for (const m of v.where('household_members', 'householdId', me.householdId)) {
    if (m.personId === me.id || !(m.role === 'child' || (opts.dependents && m.role === 'dependent'))) continue;
    const x = live(m.personId);
    if (x) add(x.id, { personId: x.id, name: x.displayName, birthDate: x.birthDate ?? null, source: 'household', linkId: null });
  }
  for (const l of v.where('family_links', 'personId', me.id)) {
    if (l.relation !== 'child') continue;
    if (l.relatedPersonId) { const x = live(l.relatedPersonId); if (x) add(x.id, { personId: x.id, name: x.displayName, birthDate: l.birthDate ?? x.birthDate ?? null, source: 'link', linkId: l.id }); }
    else add(`link:${l.id}`, { personId: null, name: l.name ?? '—', birthDate: l.birthDate ?? null, source: 'link', linkId: l.id });
  }
  for (const l of v.where('family_links', 'relatedPersonId', me.id)) {
    if (l.relation !== 'parent') continue;
    const x = live(l.personId);
    if (x) add(x.id, { personId: x.id, name: x.displayName, birthDate: x.birthDate ?? null, source: 'reverse', linkId: l.id });
  }
  return [...out.values()];
}
/** children under 18 (a child without a birth date counts: unknown age is not «grown up») */
export const minorChildren = (kids: readonly ChildRef[]) => kids.filter((k) => k.age === null || k.age < 18);
